import { ColorHelper } from './palette-color-helper';

import { getPaletteShadeHex, parseColorPaletteRaw } from './color-palette-resolve.util';

export const COLOR_PALETTE_SHADE_LEVELS = [
  1000, 900, 800, 700, 600, 500, 400, 300, 200, 100, 50, 20, 10, 5
] as const;

export interface ColorPaletteV3 {
  /** Normalized base color used to regenerate both shade families. */
  input: string;
  /** Effective generated shades, including any manual overrides. */
  primary: Record<string, string>;
  neutral: Record<string, string>;
  /** Manual shade values keyed by `primary-600`, `neutral-900`, and so on. */
  overrides?: Record<string, string>;
}

const SCALE = 600;
const SUPPORTED_SHADE_LEVELS = new Set<string>(
  COLOR_PALETTE_SHADE_LEVELS.map(level => String(level))
);

export function normalizePaletteHex(hex: string): string | null {
  const trimmed = hex.trim();
  if (!/^#(?:[\da-f]{3}|[\da-f]{6})$/i.test(trimmed)) {
    return null;
  }

  const rgb = ColorHelper.hexToRgb(trimmed);
  if (!rgb) {
    return null;
  }
  return ColorHelper.rgbToHex(...rgb).toLowerCase();
}

function sanitizePaletteOverrides(
  overrides: Record<string, string> | undefined
): Record<string, string> | undefined {
  if (!overrides) {
    return undefined;
  }

  const sanitized: Record<string, string> = {};
  for (const [flatKey, value] of Object.entries(overrides)) {
    const match = /^(primary|neutral)-(\d+)$/.exec(flatKey);
    const shadeLevel = match?.[2];
    const normalizedHex = normalizePaletteHex(value);
    if (!match?.[1] || !shadeLevel || !SUPPORTED_SHADE_LEVELS.has(shadeLevel) || !normalizedHex) {
      continue;
    }
    sanitized[`${match[1]}-${shadeLevel}`] = normalizedHex;
  }

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

function primaryColorToL(refL: number, color: number): number {
  return Math.min(
    Math.max(Math.min(1 - (1 - refL) * (1 - (SCALE - color) / SCALE), 1), 0),
    1 - color / 8000
  );
}

function neutralColorToL(color: number): number {
  return (1000 - color) / 1000;
}

function neutralColorToS(s: number, cap: number): number {
  return Math.min(s, cap);
}

/** Generates primary and neutral shades from a base hex color (OKHSL). */
export function computeAutoPaletteFromHex(
  inputHex: string
): { primary: Record<string, string>; neutral: Record<string, string> } | null {
  const hex = normalizePaletteHex(inputHex);
  if (!hex) {
    return null;
  }

  const rgb = ColorHelper.hexToRgb(hex);
  if (!rgb) {
    return null;
  }

  const okhsl = ColorHelper.srgbToOkhsl(...rgb);
  const [h, s, l] = okhsl;

  const primary: Record<string, string> = {
    1000: ColorHelper.okhslToHex(h, s, primaryColorToL(l, 1000)),
    900: ColorHelper.okhslToHex(h, s, primaryColorToL(l, 900)),
    800: ColorHelper.okhslToHex(h, s, primaryColorToL(l, 800)),
    700: ColorHelper.okhslToHex(h, s, primaryColorToL(l, 700)),
    600: ColorHelper.okhslToHex(h, s, primaryColorToL(l, 600)),
    500: ColorHelper.okhslToHex(h, s * 0.95, primaryColorToL(l, 500)),
    400: ColorHelper.okhslToHex(h, s * 0.9, primaryColorToL(l, 400)),
    300: ColorHelper.okhslToHex(h, s * 0.75, primaryColorToL(l, 300)),
    200: ColorHelper.okhslToHex(h, s * 0.65, primaryColorToL(l, 200)),
    100: ColorHelper.okhslToHex(h, s * 0.65, primaryColorToL(l, 90)),
    50: ColorHelper.okhslToHex(h, s * 0.55, primaryColorToL(l, 45)),
    20: ColorHelper.okhslToHex(h, s * 0.45, primaryColorToL(l, 18)),
    10: ColorHelper.okhslToHex(h, s * 0.4, primaryColorToL(l, 9)),
    5: ColorHelper.okhslToHex(h, s * 0.35, primaryColorToL(l, 4))
  };

  const neutral: Record<string, string> = {
    1000: ColorHelper.okhslToHex(
      h,
      neutralColorToS(s, 0.4),
      Math.min(neutralColorToL(1000), primaryColorToL(l, 1000))
    ),
    900: ColorHelper.okhslToHex(
      h,
      neutralColorToS(s, 0.4),
      Math.min(neutralColorToL(850), primaryColorToL(l, 850))
    ),
    800: ColorHelper.okhslToHex(
      h,
      neutralColorToS(s, 0.3),
      Math.min(neutralColorToL(830), primaryColorToL(l, 830))
    ),
    700: ColorHelper.okhslToHex(
      h,
      neutralColorToS(s, 0.2),
      Math.min(neutralColorToL(700), primaryColorToL(l, 700))
    ),
    600: ColorHelper.okhslToHex(
      h,
      neutralColorToS(s, 0.15),
      Math.min(neutralColorToL(600), primaryColorToL(l, 600))
    ),
    500: ColorHelper.okhslToHex(h, neutralColorToS(s, 0.1), neutralColorToL(420)),
    400: ColorHelper.okhslToHex(h, neutralColorToS(s, 0.15), neutralColorToL(180)),
    300: ColorHelper.okhslToHex(h, neutralColorToS(s, 0.25), neutralColorToL(70)),
    200: ColorHelper.okhslToHex(h, neutralColorToS(s, 0.25), neutralColorToL(50)),
    100: ColorHelper.okhslToHex(h, neutralColorToS(s, 0.2), neutralColorToL(35)),
    50: ColorHelper.okhslToHex(h, neutralColorToS(s, 0.15), neutralColorToL(20)),
    20: ColorHelper.okhslToHex(h, neutralColorToS(s, 0.1), neutralColorToL(10)),
    10: ColorHelper.okhslToHex(h, neutralColorToS(s, 0.08), neutralColorToL(5)),
    5: ColorHelper.okhslToHex(h, neutralColorToS(s, 0.05), neutralColorToL(2))
  };

  return { primary, neutral };
}

export function mergePalette(
  auto: { primary: Record<string, string>; neutral: Record<string, string> },
  overrides: Record<string, string> | undefined
): { primary: Record<string, string>; neutral: Record<string, string> } {
  const primary = { ...auto.primary };
  const neutral = { ...auto.neutral };
  const sanitizedOverrides = sanitizePaletteOverrides(overrides);

  for (const [flatKey, hex] of Object.entries(sanitizedOverrides ?? {})) {
    const primaryMatch = /^primary-(\d+)$/.exec(flatKey);
    if (primaryMatch?.[1]) {
      primary[primaryMatch[1]] = hex;
    }
    const neutralMatch = /^neutral-(\d+)$/.exec(flatKey);
    if (neutralMatch?.[1]) {
      neutral[neutralMatch[1]] = hex;
    }
  }

  return { primary, neutral };
}

export function buildStoredPalette(
  inputHex: string,
  overrides?: Record<string, string>
): ColorPaletteV3 | null {
  const hex = normalizePaletteHex(inputHex);
  if (!hex) {
    return null;
  }

  const auto = computeAutoPaletteFromHex(hex);
  if (!auto) {
    return null;
  }

  const sanitizedOverrides = sanitizePaletteOverrides(overrides);
  const merged = mergePalette(auto, sanitizedOverrides);
  const palette: ColorPaletteV3 = {
    input: hex,
    primary: merged.primary,
    neutral: merged.neutral
  };

  if (sanitizedOverrides) {
    palette.overrides = sanitizedOverrides;
  }

  return palette;
}

/** Returns merged shade hex values for palette preview swatches. */
export function palettePreviewHexes(raw: unknown): string[] {
  const parsed = parseColorPaletteRaw(raw);
  if (!parsed) {
    return [];
  }

  const merged = mergePalette(
    { primary: parsed.primary, neutral: parsed.neutral },
    parsed.overrides
  );
  const shades: string[] = [];

  for (const level of COLOR_PALETTE_SHADE_LEVELS) {
    const hex = merged.primary[String(level)];
    if (hex) {
      shades.push(hex);
    }
  }

  for (const level of COLOR_PALETTE_SHADE_LEVELS) {
    const hex = merged.neutral[String(level)];
    if (hex) {
      shades.push(hex);
    }
  }

  return shades;
}

export function readPaletteInputHex(raw: unknown): string {
  const parsed = parseColorPaletteRaw(raw);
  if (parsed) {
    return parsed.input;
  }

  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const input = (raw as Record<string, unknown>)['input'];
    if (typeof input === 'string') {
      return normalizePaletteHex(input) ?? '#3b82f6';
    }
  }

  return '#3b82f6';
}

/**
 * Resolves a palette shade for studio swatches.
 * Regenerates from `input` when stored shades are missing (journey defaults often
 * persist `{ input }` only — same path as `ColorPaletteFieldComponent`).
 */
export function resolvePaletteShadeHex(paletteValue: unknown, flatKey: string): string | undefined {
  const direct = getPaletteShadeHex(paletteValue, flatKey);
  if (direct) {
    return direct;
  }

  const generated = buildStoredPalette(readPaletteInputHex(paletteValue));
  return generated ? getPaletteShadeHex(generated, flatKey) : undefined;
}
